import React from "react";
import ToggleSwitch from "./ui/ToggleSwitch";
import InputWithSuffix from "./ui/InputWithSuffix";
import PillSelector from "./ui/PillSelector";
import BasketSecuritySection from "./BasketSecuritySection";
import LtvPreview from "./LtvPreview";

/**
 * Tab 3 — בטחונות (Collateral & Guarantees)
 */
export default function LoanFormTab3({
  loan,
  displayValues,
  errors,
  touched,
  onChange,
  onBlur,
  onBlurNumber,
  security,
  onChangeSecurity,
  isEditMode,
  existingBasket,
  loanId,
}) {
  return (
    <div className="form-grid">
      {/* ── Basket / Security Section ────────────────────── */}
      <div className="field form-full-width">
        <BasketSecuritySection
          security={security}
          onChange={onChangeSecurity}
          isEditMode={isEditMode}
          existingBasket={existingBasket}
          loanId={loanId}
        />
      </div>

      {/* Answers "what will the LTV be?" while the collateral is still being typed,
          instead of only after the loan is saved. */}
      {!isEditMode && <LtvPreview loan={loan} security={security} />}

      {/* ── Pledge Details ──────────────────────────────── */}
      <h3 className="form-section-header">פרטי שעבוד</h3>

      {/* The pledging company is recorded per pledge in the asset registry
          (Pledge.pledgingCompany, defaulted from the asset's owning company),
          not as a single free-text field on the loan. */}

      <div className="field">
        <label className="label-optional">פיקדון מזומן בבנק (₪) <span className="optional-badge">(אופציונלי)</span></label>
        <input
          className="input"
          name="cashDepositAmount"
          type="text"
          value={displayValues.cashDepositAmount}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
        <small style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "4px", display: "block" }}>
          מזומן המופקד בבנק ומקזז את החוב נטו לחישוב LTV
        </small>
      </div>

      {/* ── LTV Thresholds — shown only when not joining an existing basket ── */}
      {security.basketMode !== "EXISTING" && (
        <>
          <h3 className="form-section-header">ספי LTV</h3>

          {/* These values live on the basket, and only the create path writes them
              (pickLtvConfig reads them off the POST body). On edit the loan PUT drops
              them, so show the basket's current values but lock them. */}
          {isEditMode && (
            <div className="field form-full-width ltv-config-locked-note">
              ספי ה-LTV והקובנאנט מנוהלים ברמת המסגרת. לעריכה — פתחו את ההלוואה ועברו ללשונית «בטחונות».
            </div>
          )}

          <fieldset className="ltv-config-fields" disabled={isEditMode}>
          <div className="field form-full-width">
            <label className="label-optional">שיטת חישוב מחיר לLTV <span className="optional-badge">(אופציונלי)</span></label>
            <PillSelector
              name="ltvPriceFormula"
              value={loan.ltvPriceFormula}
              options={[
                { value: "SPOT", label: "שער ידוע" },
                { value: "3DAY_AVG", label: "ממוצע 3 ימים" },
                { value: "5DAY_AVG", label: "ממוצע 5 ימים" },
                { value: "90DAY_AVG", label: "ממוצע 90 יום" },
              ]}
              onChange={onChange}
            />
            <small style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "6px", display: "block" }}>
              כפי שמוגדר בהסכם עם הבנק — משפיע על חישוב ה-LTV בדוח הבטחונות
            </small>
          </div>

          <div className="field">
            <label className="label-optional">סף אזהרה — LTV להוספת בטוחות <span className="optional-badge">(אופציונלי)</span></label>
            <InputWithSuffix
              name="ltvWarningThreshold"
              value={displayValues.ltvWarningThreshold}
              onChange={onChange}
              onBlur={onBlurNumber}
              placeholder="0"
            />
            <small style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "4px", display: "block" }}>
              LTV שבו הבנק דורש השלמת בטוחות
            </small>
            {touched.ltvWarningThreshold && errors.ltvWarningThreshold && (
              <div className="field-error">{errors.ltvWarningThreshold}</div>
            )}
          </div>

          <div className="field">
            <label className="label-optional">סף הפרה — LTV לפירעון מיידי <span className="optional-badge">(אופציונלי)</span></label>
            <InputWithSuffix
              name="ltvBreachThreshold"
              value={displayValues.ltvBreachThreshold}
              onChange={onChange}
              onBlur={onBlurNumber}
              placeholder="0"
            />
            <small style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "4px", display: "block" }}>
              LTV בו ההלוואה נחשבת מופרת ויש עילה לפירעון מיידי
            </small>
            {touched.ltvBreachThreshold && errors.ltvBreachThreshold && (
              <div className="field-error">{errors.ltvBreachThreshold}</div>
            )}
          </div>

          <div className="field form-full-width">
            <ToggleSwitch
              name="canReleaseStockAtThreshold"
              checked={loan.canReleaseStockAtThreshold}
              onChange={onChange}
              label="ניתן לשחרר מניות ברף LTV מסוים"
            />
          </div>

          {loan.canReleaseStockAtThreshold && (
            <>
              <div className="field">
                <label className="label-required">LTV מינימלי לשחרור</label>
                <InputWithSuffix
                  name="stockReleaseLTVMin"
                  value={displayValues.stockReleaseLTVMin}
                  onChange={onChange}
                  onBlur={onBlurNumber}
                  placeholder="0"
                />
                {touched.stockReleaseLTVMin && errors.stockReleaseLTVMin && (
                  <div className="field-error">{errors.stockReleaseLTVMin}</div>
                )}
              </div>

              <div className="field">
                <label className="label-required">LTV מקסימלי לשחרור</label>
                <InputWithSuffix
                  name="stockReleaseLTVMax"
                  value={displayValues.stockReleaseLTVMax}
                  onChange={onChange}
                  onBlur={onBlurNumber}
                  placeholder="0"
                />
                {touched.stockReleaseLTVMax && errors.stockReleaseLTVMax && (
                  <div className="field-error">{errors.stockReleaseLTVMax}</div>
                )}
              </div>
            </>
          )}

          {/* ── Covenant Text ─────────────────────────────────── */}
          <h3 className="form-section-header">טקסט קובנאנט</h3>

          <div className="field form-full-width">
            <label className="label-optional">טקסט הסכם <span className="optional-badge">(אופציונלי)</span></label>
            <textarea
              className="textarea"
              name="covenantText"
              value={loan.covenantText}
              onChange={onChange}
              rows={5}
              placeholder="הדבק כאן את הסעיפים הרלוונטיים מהסכם ההלוואה המגדירים את תנאי הבטוחה, סף ה-LTV, ומנגנון ההשלמה..."
            />
            <small style={{ color: "var(--text-secondary)", fontSize: "12px", marginTop: "4px", display: "block" }}>
              הטקסט יוצג בדוח הבטחונות בעת לחיצה על ההלוואה
            </small>
          </div>
          </fieldset>
        </>
      )}
    </div>
  );
}
